import React, { useState, useEffect } from "react";
import axios from "axios";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faSearch } from "@fortawesome/free-solid-svg-icons";
import Grid from "../components/Grid";
import { FaEdit } from "react-icons/fa";
import { toast } from "react-toastify";
import { saveAs } from "file-saver";
import { FaDownload } from "react-icons/fa"; 
import ExcelJS from "exceljs";

const API_BASE_URL = process.env.REACT_APP_API_BASE_URL;

function ProductCategory({ onAddProductCategoryClick, onAddLocationCategoryClick, onAddAdjustmentCategoryClick }) {
  const [categories, setCategories] = useState([]);
  const [locationCategories, setLocationCategories] = useState([]);
  const [adjustmentCategories, setAdjustmentCategories] = useState([]);
  const [selectedCategory, setSelectedCategory] = useState(null);
  const [searchTerm, setSearchTerm] = useState("");
  const [sort] = useState({ field: "category_name", direction: "asc" });
  const [selectedSearchField, setSelectedSearchField] = useState("category_name");
  const [activeTab, setActiveTab] = useState("product");
  const [userRole, setUserRole] = useState("");

  useEffect(() => {
    const fetchUserRole = async () => {
      try {
        const token = sessionStorage.getItem("token");
        if (!token) {
          toast.error("Authentication token is missing. Please login again.");
          return;
        }

        const userResponse = await axios.get(`${API_BASE_URL}/users/specific`, {
          headers: {
            Authorization: `Bearer ${token}`,
          },
        });

        setUserRole(userResponse.data.role);
      } catch(error) {
        console.error("Error fetching user role: ", error);
        toast.error("Error fetching user role");
      }
    };

    fetchUserRole();
  }, []);

  useEffect(() => {
    const fetchData = async () => {
      try {
        const token = sessionStorage.getItem("token");
  
        if (!token) {
          toast.error("No token found. Please log in.");
          return;
        }
  
        // Fetch both categories simultaneously
        const [productResponse, locationResponse, adjustmentResponse] = await Promise.all([
          axios.get(`${API_BASE_URL}/categories`, {
            headers: { Authorization: `Bearer ${token}` },
          }),
          axios.get(`${API_BASE_URL}/locationTypes`, {
            headers: { Authorization: `Bearer ${token}` },
          }),
          axios.get(`${API_BASE_URL}/adjustmentTypes`, {
            headers: { Authorization: `Bearer ${token}` },
          }
          )
        ]);

      if (!Array.isArray(productResponse.data.categories)) {
        console.error("Invalid product categories format:", productResponse.data);
      }

      if (!Array.isArray(locationResponse.data.locationCategories)) {
        console.error("Invalid location categories format:", locationResponse.data);
      }

      if (!Array.isArray(adjustmentResponse.data.adjustmentCategories)) {
        console.error("Invalid adjustment categories format:", adjustmentResponse.data);
      }

      setCategories(productResponse.data.categories || []);
      if (Array.isArray(locationResponse.data.locationCategories)) {
        setLocationCategories(
          locationResponse.data.locationCategories.map((category) => ({
            ...category,
            id: category.location_category_code,
            category_code: category.location_category_code,
            category_name: category.location_category_name,
            status: category.status || "Active",
          }))
        );
      } else {
        console.error("Invalid location categories format:", locationResponse.data);
        setLocationCategories([]); // Ensure it's an empty array instead of undefined
      }

      if (Array.isArray(adjustmentResponse.data.adjustmentCategories)) {
        setAdjustmentCategories(
          adjustmentResponse.data.adjustmentCategories.map((category) => ({
            ...category,
            id: category.adjustment_category_code,
            category_code: category.adjustment_category_code,
            category_name: category.adjustment_category_name,
            status: category.status ,
          }))
        );
      } else {
        console.error("Invalid location categories format:", adjustmentResponse.data);
        setAdjustmentCategories([]); // Ensure it's an empty array instead of undefined
      }

      // if (Array.isArray(locationResponse.data.locationCategories)) {
      //   setLocationCategories(locationResponse.data.locationCategories.map((category) => ({
      //     ...category,
      //     id: category.location_category_code, // Ensure it has a valid ID
      //   })));
      // } else {
      //   console.error("Invalid location categories format:", locationResponse.data);
      // }
      // console.log("Location Categories:", locationCategories);
  
        // Store categories correctly
        // setCategories(productResponse.data.categories.map((category) => ({
        //   ...category,
        //   id: category.category_code,
        // })));

  
      } catch (error) {
        console.error("Error fetching categories:", error);
        toast.error("Error fetching categories");
      }
    };
  
    fetchData();
  }, []); // Runs only once on mount

  const handleCategoryClick = (category) => {
    setSelectedCategory({ ...category, status: category.status || "Active" });
  };

  const handleClosePopup = () => setSelectedCategory(null);

  const handleStatusUpdate = async () => {
    if (!selectedCategory) return;
    try {
      const url = activeTab === "product"
        ? `${API_BASE_URL}/categories/update/${selectedCategory.category_code}`
        : activeTab === "location"
        ? `${API_BASE_URL}/locationTypes/update/${selectedCategory.category_code}`
        : `${API_BASE_URL}/adjustmentTypes/update/${selectedCategory.category_code}`;
  
      const response = await axios.put(url, { status: selectedCategory.status });
  
      toast.success(response.data.message);
      setSelectedCategory(null);
  
      // Refresh both categories
      const [productRefresh, locationRefresh, adjustmentRefresh] = await Promise.all([
        axios.get(`${API_BASE_URL}/categories`),
        axios.get(`${API_BASE_URL}/locationTypes`),
        axios.get(`${API_BASE_URL}/adjustmentTypes`),
      ]);
  
      setCategories(productRefresh.data.categories.map((category) => ({
        ...category,
        id: category.category_code,
      })));
  
      setLocationCategories(locationRefresh.data.locationCategories.map((category) => ({
        ...category,
        id: category.location_category_code,
        category_code: category.location_category_code,
        category_name: category.location_category_name,
        status: category.status || "Active",
      })));

      setAdjustmentCategories(adjustmentRefresh.data.adjustmentCategories.map((category) => ({
        ...category,
        id: category.adjustment_category_code,
        category_code: category.adjustment_category_code,
        category_name: category.adjustment_category_name,
        status: category.status || "Active",
      })));
      
    } catch (error) {
      console.error("Error updating category status:", error);
      toast.error("Error updating category status");
    }
  };

  const activeCategories = activeTab === "product" ? categories : activeTab === "location" ? locationCategories : adjustmentCategories;

  const handleDownloadExcel = async () => {
    if (filteredCategories.length === 0) {
      toast.warn("No categories to export.");
      return;
    }

    const workbook = new ExcelJS.Workbook();
    const worksheet = workbook.addWorksheet(
      activeTab === "product"
        ? "Product Categories"
        : activeTab === "location"
        ? "Location Categories"
        : "Stock Adjustment Categories"
    );

    // Define columns and headers per active tab
    let headers = [];
    if (activeTab === "product") {
      headers = ["Category Code", "Category Name", "Status"];
    } else if (activeTab === "location") {
      headers = ["Location Category Code", "Location Category Name", "Status"];
    } else {
      headers = [
        "Adjustment Category Code",
        "Adjustment Category Name",
        "Stock Movement Type",
        "Status",
      ];
    }

    worksheet.addRow(headers);

    // Style header row
    const headerRow = worksheet.getRow(1);
    headerRow.eachCell((cell) => {
      cell.fill = {
        type: "pattern",
        pattern: "solid",
        fgColor: { argb: "FFD966" }, // Light yellow
      };
      cell.font = { bold: true };
      cell.border = {
        top: { style: "thin" },
        left: { style: "thin" },
        bottom: { style: "thin" },
        right: { style: "thin" },
      };
    });

    // Add data rows
    filteredCategories.forEach((category) => {
      if (activeTab === "product") {
        worksheet.addRow([
          category.category_code,
          category.category_name,
          category.status,
        ]);
      } else if (activeTab === "location") {
        worksheet.addRow([
          category.location_category_code,
          category.location_category_name,
          category.status,
        ]);
      } else {
        // adjustment tab
        const movementType =
          category.stock_movement_type?.toLowerCase() === "add"
            ? "Addition"
            : category.stock_movement_type?.toLowerCase() === "remove"
            ? "Removal"
            : category.stock_movement_type || "";

        worksheet.addRow([
          category.adjustment_category_code,
          category.adjustment_category_name,
          movementType,
          category.status,
        ]);
      }
    });

    // Adjust column widths automatically based on header or content length
    worksheet.columns.forEach((column, i) => {
      let maxLength = headers[i].length;
      column.eachCell({ includeEmpty: true }, (cell) => {
        const columnLength = cell.value ? cell.value.toString().length : 0;
        if (columnLength > maxLength) maxLength = columnLength;
      });
      column.width = maxLength < 20 ? 20 : maxLength;
    });

    // Generate and save Excel file
    const buffer = await workbook.xlsx.writeBuffer();
    const blob = new Blob([buffer], {
      type:
        "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet",
    });
    saveAs(
      blob,
      `${activeTab === "product" ? "Product_Categories" : activeTab === "location" ? "Location_Categories" : "Stock_Adjustment_Categories"
      }.xlsx`
    );
  };

  const filteredCategories = activeCategories
  .filter((category) =>
    category[selectedSearchField]?.toLowerCase().includes(searchTerm.toLowerCase())
  )
  .sort((a, b) => (a[sort.field] < b[sort.field] ? -1 : 1));


    const columns = activeTab === "product" ? [
      { field: "category_code", headerName: "ID", flex: 1 },
      { field: "category_name", headerName: "Category Name", flex: 1 },
      {
        field: "status",
        headerName: "Status",
        flex:1,
        renderCell: (params) => {
          const status = params.row.status;
          const statusClass = status === "Active" ? "bg-[#388E3C] text-white" : "bg-[#E57373] text-white";
          return <span className={`px-2 py-1 rounded-sm ${statusClass}`}>{status}</span>;
        },
      },
      {
        field: "action",
        headerName: "Action",
        flex: 1,
        renderCell: (params) => (
          <div className="flex justify-left items-left w-10 h-10">
            <button onClick={() => handleCategoryClick(params.row)} className="bg-[#28ad4b] text-white px-2 py-1 flex items-center rounded-lg">
              <FaEdit className="text-white mr-2" />
              Edit
            </button>
          </div>
        ),
      },
    ] : activeTab === "location" ? [
      { field: "location_category_code", headerName: "ID", flex:1 },
      { field: "location_category_name", headerName: "Category Name", flex: 1 },
      {
        field: "status",
        headerName: "Status",
        flex: 1,
        renderCell: (params) => {
          const status = params.row.status;
          const statusClass = status === "Active" ? "bg-[#388E3C] text-white" : "bg-[#E57373] text-white";
          return <span className={`px-2 py-1 rounded-sm ${statusClass}`}>{status}</span>;
        },
      },
      {
        field: "action",
        headerName: "Action",
        flex: 1,
        renderCell: (params) => (
          <div className="flex justify-left items-left w-10 h-10">
            <button onClick={() => handleCategoryClick(params.row)} className="bg-[#28ad4b] text-white px-2 py-1 flex items-center rounded-lg">
              <FaEdit className="text-white mr-2" />
              Edit
            </button>
          </div>
        ),
      },
    ] : [
      { field: "adjustment_category_code", headerName: "ID", flex: 1 },
      { field: "adjustment_category_name", headerName: "Category Name", flex: 1 },
      {
        field: "stock_movement_type",
        headerName: "Stock Movement Type",
        flex: 1,
        renderCell: (params) => {
          const movementType = params.row.stock_movement_type.toLowerCase();
          const displayText = movementType === "add" ? "Addition" : movementType === "remove" ? "Removal" : params.row.stock_movement_type;
          const movementClass = displayText === "Addition" ? "bg-green-400 text-white" : displayText === "Removal" ? "bg-red-400 text-white" : "";
      
          return <span className={`px-2 py-1 rounded-sm ${movementClass}`}>{displayText}</span>;
        },
      },      
      {
        field: "status",
        headerName: "Status",
        flex: 1,
        renderCell: (params) => {
          const status = params.row.status;
          const statusClass = status === "Active" ? "bg-[#388E3C] text-white" : "bg-red-400 text-white";
          return <span className={`px-2 py-1 rounded-sm ${statusClass}`}>{status}</span>;
        },
      },
      {
        field: "action",
        headerName: "Action",
        flex: 1,
        renderCell: (params) => (
          <div className="flex justify-left items-left w-10 h-10">
            <button onClick={() => handleCategoryClick(params.row)} className="bg-[#28ad4b] text-white px-2 py-1 flex items-center rounded-lg">
              <FaEdit className="text-white mr-2" />
              Edit
            </button>
          </div>
        ),
      },
    ];

  return (
    <div className="flex h-screen">
      <div className="flex-1 p-8">
        {/* Tab Buttons */}
        <div className="flex space-x-4 mb-6">
          <button
            onClick={() => setActiveTab("product")}
            className={`px-4 py-2 rounded-lg ${activeTab === "product" ? "bg-green-700 text-white" : "bg-gray-300"}`}
          >
            Product Category
          </button>
          <button
            onClick={() => setActiveTab("location")}
            className={`px-4 py-2 rounded-lg ${activeTab === "location" ? "bg-green-700 text-white" : "bg-gray-300"}`}
          >
            Location Category
          </button>
          <button
            onClick={() => setActiveTab("adjustment")}
            className={`px-4 py-2 rounded-lg ${activeTab === "adjustment" ? "bg-green-700 text-white" : "bg-gray-300"}`}
          >
            Stock Adjustment Category
          </button>
        </div>

        {/* Dynamic Add Button */}
        <div className="flex justify-between items-center mb-8">
          <h1 className="text-3xl font-bold text-green-700">
            {activeTab === "product" ? "Product Categories" : activeTab === "location" ? "Location Categories" : "Stock Adjustment Categories" }
          </h1>
          { (userRole === "Company Admin" || userRole === "Main Admin") && activeTab === "product" ? (
            <button onClick={onAddProductCategoryClick} className="bg-green-700 text-white px-4 py-2 rounded-lg">
              Add Product Category
            </button>
          ) : activeTab === "location" ? (
            <button onClick={onAddLocationCategoryClick} className="bg-green-700 text-white px-4 py-2 rounded-lg">
              Add Location Category
            </button>
          ) : (
            <button onClick={onAddAdjustmentCategoryClick} className="bg-green-700 text-white px-4 py-2 rounded-lg">
              Add Adjustment Category
            </button>
          )}
        </div>
        {/* Search & Table */}
        <div className="bg-white p-4 rounded-lg shadow">
          <div className="flex justify-between mb-4 items-center flex-wrap gap-4">
            {/* Left Section: Search filters */}
            <div className="flex items-center flex-wrap gap-4">
              <select
                className="px-4 py-2 border border-gray-300 rounded-lg"
                value={selectedSearchField}
                onChange={(e) => setSelectedSearchField(e.target.value)}
              >
                <option value="category_name">Category Name</option>
                <option value="category_code">Category Code</option>
                <option value="status">Status</option>
                {activeTab === "adjustment" && <option value="stock_movement_type">Stock Movement Type</option>}
              </select>

              <div className="relative">
                <input
                  type="text"
                  placeholder="Search Category"
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                  className="px-4 py-2 pl-10 border border-gray-300 rounded-lg w-full max-w-xs"
                />
                <div className="absolute inset-y-0 left-0 flex items-center pl-3">
                  <FontAwesomeIcon icon={faSearch} className="text-gray-500" />
                </div>
              </div>
            </div>

            {/* Right Section: Download Excel */}
            <div>
              <button
                onClick={handleDownloadExcel}
                className="flex items-center bg-green-600 text-white px-4 py-2 rounded-lg hover:bg-green-700"
              >
                <FaDownload className="mr-2" />
                Download Excel
              </button>
            </div>
          </div>         

          <Grid rows={filteredCategories} columns={columns} getRowId={(row) => row.category_code}/>
        </div>

        {/* Edit Popup */}
        {selectedCategory && (
          <div className="fixed inset-0 flex items-center justify-center bg-gray-900 bg-opacity-50 z-50">
            <div className="bg-white p-8 rounded-lg shadow-lg w-full md:w-2/3 lg:w-1/2 relative">
              <button
                onClick={handleClosePopup}
                className="absolute top-2 right-2 bg-red-500 text-white w-8 h-8 flex justify-center items-center rounded-sm hover:bg-red-700"
              >
                &times;
              </button>
              <div className="flex mb-4">
                <div className="w-1/2 pl-4">
                  <h2 className="text-xl font-semibold mb-4">{selectedCategory.category_code}</h2>
                  <div className="mb-4">
                    <label className="block text-gray-700">Category Name</label>
                    <input type="text" value={selectedCategory.category_name} className="w-full px-4 py-2 border border-gray-300 rounded-lg"  />
                  </div>
                  <button onClick={handleStatusUpdate} className="bg-green-700 text-white px-4 py-2 rounded-lg">
                    Update
                  </button>
                </div>
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}

export default ProductCategory;
